import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {BuilderStateService} from './services/builder-state.service';
import {ActivatedRoute, Router} from '@angular/router';
import {FormService} from '../shared/services/form.service';
import {Subscription} from 'rxjs';
import {FormPreviewSplitViewComponent} from "./components/form-preview-split-view/form-preview-split-view.component";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {FormStatus} from "../shared/models/form-schema.model";

@Component({
  selector: 'forms-builder',
  standalone: true,
  imports: [CommonModule, FormPreviewSplitViewComponent],
  template: `
    <div class="absolute inset-0 flex flex-col bg-gray-50/50">
      <!-- Builder toolbar -->
      <header
        class="flex-shrink-0 flex items-center gap-2 sm:gap-4 px-3 sm:px-5 h-14 bg-white border-b border-gray-200 shadow-sm z-30">
        <button (click)="goBack()" title="Zurück zur Übersicht"
                class="flex-shrink-0 p-2 -ml-1 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-all">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>

        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2 min-w-0">
            <h1 class="truncate text-sm font-bold text-gray-900 tracking-tight">{{ state.currentDefinition.title }}</h1>
            <span [class]="'flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ' + statusClass()">
              {{ statusLabel() }}
            </span>
            <span *ngIf="dirty"
                  class="flex-shrink-0 hidden sm:inline-flex items-center gap-1 text-[10px] font-bold text-amber-500 uppercase tracking-wider">
              <span class="w-1.5 h-1.5 bg-amber-400 rounded-full"></span> Ungespeichert
            </span>
          </div>
        </div>

        <!-- Undo / redo -->
        <div class="flex items-center rounded-lg bg-gray-100 p-0.5">
          <button (click)="state.undo()" [disabled]="!canUndo" title="Rückgängig (Strg+Z)"
                  class="p-1.5 rounded-md text-gray-500 enabled:hover:bg-white enabled:hover:text-gray-900 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a5 5 0 010 10h-1M3 10l4-4M3 10l4 4"/>
            </svg>
          </button>
          <button (click)="state.redo()" [disabled]="!canRedo" title="Wiederholen (Strg+Umschalt+Z)"
                  class="p-1.5 rounded-md text-gray-500 enabled:hover:bg-white enabled:hover:text-gray-900 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 10H11a5 5 0 000 10h1M21 10l-4-4M21 10l-4 4"/>
            </svg>
          </button>
        </div>

        <!-- Publish toggle -->
        <button (click)="togglePublish()"
                class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all active:scale-95"
                [class]="isPublished()
                  ? 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  : 'border-green-200 text-green-700 bg-green-50 hover:bg-green-100'">
          {{ isPublished() ? 'Zurückziehen' : 'Veröffentlichen' }}
        </button>

        <!-- Save -->
        <button (click)="saveForm()" [disabled]="saving"
                class="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold text-white bg-theme-primary-600 hover:bg-theme-primary-700 shadow-sm shadow-theme-primary-100 transition-all active:scale-95 disabled:opacity-50">
          <svg *ngIf="!saving" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          {{ saving ? 'Speichern…' : 'Speichern' }}
        </button>
      </header>

      <!-- Editor surface -->
      <forms-form-preview-split-view class="flex-1 min-h-0"></forms-form-preview-split-view>
    </div>
  `
})
export class BuilderComponent implements OnInit, OnDestroy {
  dirty = false;
  canUndo = false;
  canRedo = false;
  saving = false;

  private sub = new Subscription();

  constructor(
    public state: BuilderStateService,
    private formService: FormService,
    private route: ActivatedRoute,
    private router: Router,
    private alert: GlobalAlertService
  ) {
  }

  ngOnInit() {
    this.sub.add(this.route.queryParams.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.formService.getForm(id).subscribe(form => {
          if (form) {
            this.state.loadDefinition(JSON.parse(JSON.stringify(form)));
          }
        });
      }
    }));
    this.sub.add(this.state.dirty$.subscribe(d => this.dirty = d));
    this.sub.add(this.state.canUndo$.subscribe(v => this.canUndo = v));
    this.sub.add(this.state.canRedo$.subscribe(v => this.canRedo = v));
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  /** Used by the CanDeactivate guard to warn about unsaved changes. */
  hasUnsavedChanges(): boolean {
    return this.state.isDirty;
  }

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    const meta = event.ctrlKey || event.metaKey;
    if (!meta) return;
    const key = event.key.toLowerCase();
    if (key === 's') {
      event.preventDefault();
      this.saveForm();
    } else if (key === 'z' && !event.shiftKey) {
      event.preventDefault();
      this.state.undo();
    } else if ((key === 'z' && event.shiftKey) || key === 'y') {
      event.preventDefault();
      this.state.redo();
    }
  }

  isPublished(): boolean {
    return this.state.currentDefinition.status === 'published';
  }

  statusLabel(): string {
    return ({draft: 'Entwurf', published: 'Veröffentlicht', archived: 'Archiviert'} as Record<FormStatus, string>)
      [this.state.currentDefinition.status] || this.state.currentDefinition.status;
  }

  statusClass(): string {
    switch (this.state.currentDefinition.status) {
      case 'published':
        return 'bg-green-100 text-green-700';
      case 'archived':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-orange-100 text-orange-700';
    }
  }

  saveForm() {
    if (this.saving) return;
    this.saving = true;
    const def = this.state.currentDefinition;
    this.formService.updateForm(def).subscribe({
      next: () => {
        this.state.markSaved();
        this.saving = false;
        this.alert.createAndShow("Gespeichert", "Das Formular wurde gespeichert.", AlertLevel.success);
      },
      error: () => {
        this.saving = false;
        this.alert.createAndShow("Fehler", "Das Formular konnte nicht gespeichert werden.", AlertLevel.error);
      }
    });
  }

  togglePublish() {
    const def = this.state.currentDefinition;
    const status: FormStatus = def.status === 'published' ? 'draft' : 'published';
    this.state.setDefinition({...def, status});
    this.saveForm();
  }

  goBack() {
    this.router.navigate(['/company/forms']);
  }
}
